import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Grow,
  ListSubheader,
  Menu,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import { Placement } from '@popperjs/core';
import { find, map } from 'lodash';
import React from 'react';

import { ActionDialog, useActiveActionDialogType } from 'components/ActionDialog';
import { ActionMenuItem } from 'components/StudyDashboard/ProceduresPage/Actions/ActionMenuItem';
import { useCasesViewMode } from 'components/StudyDashboard/ProceduresPage/useCasesViewMode';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { ActionMenuItemOption, useActionMenuItemOptions } from './useActionMenuItemOptions';

interface Props {
  totalRows: number;
  currentStains?: string[];
  disablePortal?: boolean;
  placement?: Placement;
  compact?: boolean;
}

const ActionsMenu: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  totalRows,
  currentStains,
  disablePortal = true,
  placement = 'bottom-end',
  compact = false,
}) => {
  const [currentActionItemType, setCurrentActionItemType] = useActiveActionDialogType();
  const { casesViewMode } = useCasesViewMode();

  const { noneSelected } = useRowSelectionContext();
  const noRows = totalRows === 0;
  const disableRowOperations = noRows || noneSelected(totalRows);
  const actionTriggerTooltip = disableRowOperations ? 'Select procedures/slides' : 'Actions';

  const {
    actionItems,
    isLoading,
    anchorEl,
    submenuAnchorEl,
    open,
    handleClick,
    handleSubmenuOpen,
    handleSubmenuClose,
    handleMenuClose,
  } = useActionMenuItemOptions({ currentStains });

  const currentActionItem = find(actionItems, ({ type }) => Boolean(currentActionItemType === type));

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    actionItem: ActionMenuItemOption
  ) => {
    setCurrentActionItemType(actionItem.type);
    if (actionItem.subMenuItems) {
      handleSubmenuOpen(event);
      return;
    }

    handleMenuClose();
    handleSubmenuClose();
  };

  const actionTrigger = (
    <Tooltip title={actionTriggerTooltip}>
      <span>
        <Button
          color="primary"
          variant="outlined"
          size="small"
          disabled={disableRowOperations}
          onClick={handleClick}
          startIcon={isLoading ? <CircularProgress color="inherit" size={18} /> : <PlayCircleOutlineIcon />}
          data-cy="actions-menu-button"
          sx={{
            whiteSpace: 'nowrap',
            ...(compact ? { px: 1, minWidth: 'auto' } : {}),
          }}
        >
          Actions
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <ClickAwayListener
      onClickAway={() => {
        handleMenuClose();
        handleSubmenuClose();
      }}
    >
      <div>
        {actionTrigger}

        <Popper
          data-testid="actions-menu-popper"
          open={open}
          anchorEl={anchorEl}
          role={undefined}
          placement={placement}
          transition
          disablePortal={disablePortal}
          sx={{
            maxHeight: '60vh',
            overflow: 'auto',
            zIndex: (theme) => theme.zIndex.modal,
          }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'left bottom' }}>
              <Paper>
                <MenuList autoFocusItem={open} id="composition-menu" aria-labelledby="composition-button">
                  {map(actionItems, (menuItem, index) => {
                    const key = `${index}-${menuItem.header}`;
                    return (
                      !menuItem.hide &&
                      (menuItem.customMenuItem ? (
                        <React.Fragment key={key}>{menuItem.customMenuItem}</React.Fragment>
                      ) : menuItem.isListSubheader ? (
                        <ListSubheader key={key}>{menuItem.header}</ListSubheader>
                      ) : (
                        <ActionMenuItem
                          key={key}
                          disabled={disableRowOperations || menuItem.disabled}
                          header={menuItem.header}
                          onClick={(event) => {
                            if (menuItem.onClick) {
                              menuItem.onClick(event);
                              return;
                            }
                            handleMenuItemClick(event, menuItem);
                          }}
                          hasSubmenu={Boolean(menuItem.subMenuItems)}
                        />
                      ))
                    );
                  })}
                </MenuList>
              </Paper>
            </Grow>
          )}
        </Popper>
        <Menu
          anchorEl={submenuAnchorEl}
          open={Boolean(submenuAnchorEl)}
          onClose={handleSubmenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: casesViewMode === 'table' ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: casesViewMode === 'table' ? 'right' : 'left' }}
          slotProps={{
            paper: {
              style: {
                maxHeight: 400,
                maxWidth: 500,
              },
            },
          }}
        >
          {map(currentActionItem?.subMenuItems, (subMenuItem) => {
            return (
              !subMenuItem.hide &&
              (subMenuItem.customMenuItem || (
                <ActionMenuItem
                  key={subMenuItem.header}
                  disabled={disableRowOperations || subMenuItem.disabled}
                  header={subMenuItem.header}
                  onClick={(event) => {
                    if (subMenuItem.onClick) {
                      subMenuItem.onClick(event);
                      return;
                    }
                    handleMenuItemClick(event, subMenuItem);
                  }}
                />
              ))
            );
          })}
        </Menu>
        <ActionDialog />
      </div>
    </ClickAwayListener>
  );
};

export default ActionsMenu;
